import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { User, Phone, Calendar, Star, Heart, Clock, ArrowLeft, LogOut, Award, Pencil, MapPin, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';


const API = process.env.REACT_APP_BACKEND_URL || '/api';

function CustomerProfile() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  const [userName, setUserName] = useState(localStorage.getItem('userName') || 'Customer');
  const [userPhone, setUserPhone] = useState(localStorage.getItem('userPhone') || '');
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');
  const [userAddress, setUserAddress] = useState(localStorage.getItem('userAddress') || '');
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    address: '',
    email: ''
  });

  useEffect(() => {
    fetchUserData();
    fetchUserProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserProfile = async () => {
    try {
      if (userPhone) {
        const response = await axios.get(`${API}/user/profile/${encodeURIComponent(userPhone)}`);
        if (response.data) {
          if (response.data.name) {
            setUserName(response.data.name);
            localStorage.setItem('userName', response.data.name);
          }
          if (response.data.address) {
            setUserAddress(response.data.address);
            localStorage.setItem('userAddress', response.data.address);
          }
          if (response.data.email) {
            setUserEmail(response.data.email);
            localStorage.setItem('userEmail', response.data.email);
          }
        }
      }
    } catch (error) {
      console.log('Profile fetch error:', error);
    }
  };

  const fetchUserData = async () => {
    setLoading(true);
    try {
      // Fetch bookings
      if (userPhone) {
        const bookingsRes = await axios.get(`${API}/bookings/customer/${encodeURIComponent(userPhone)}`);
        const bookingsData = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];
        setBookings(bookingsData);
        
        // Calculate loyalty points (10 points per completed booking)
        const completedCount = bookingsData.filter(b => b.status === 'completed').length;
        setLoyaltyPoints(completedCount * 10);
      }
      
      // Fetch favorites
      try {
        const firebaseUid = localStorage.getItem('firebaseUid');
        if (firebaseUid) {
          const favRes = await axios.get(`${API}/customer/${firebaseUid}/favorites`);
          setFavorites(Array.isArray(favRes.data) ? favRes.data : []);
        }
      } catch (err) {
        setFavorites([]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
    setLoading(false);
  };

  const openEditDialog = () => {
    setEditForm({
      name: userName || '',
      address: userAddress || '',
      email: userEmail || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/user/update-profile`, {
        phone: userPhone,
        firebase_uid: localStorage.getItem('firebaseUid'),
        name: editForm.name,
        address: editForm.address,
        email: editForm.email
      });

      if (response.data) {
        // Update local state and localStorage
        setUserName(editForm.name);
        setUserAddress(editForm.address);
        setUserEmail(editForm.email);
        
        localStorage.setItem('userName', editForm.name);
        localStorage.setItem('userAddress', editForm.address);
        localStorage.setItem('userEmail', editForm.email);
        
        toast.success('Profile updated successfully ✅');
        setShowEditDialog(false);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.response?.data?.error || 'Failed to update profile');
    }
    setSaving(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const upcomingBookings = bookings.filter(b => 
    b && new Date(b.booking_date) >= new Date() && b.status !== 'cancelled' && b.status !== 'completed'
  );
  
  const pastBookings = bookings.filter(b => 
    b && (new Date(b.booking_date) < new Date() || b.status === 'completed')
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
        <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{background:'#f8f5ff'}}>
      {/* Premium Header */}
      <div style={{background:'rgba(255,255,255,0.95)',backdropFilter:'blur(16px)',borderBottom:'1px solid #f3f4f6',padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:10}}>
        <button onClick={() => navigate('/customer')} style={{background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:'6px',color:'#374151',fontWeight:600,fontSize:'0.85rem'}}>
          <ArrowLeft size={18}/> Back
        </button>
        <Logo size="small" />
        <button onClick={handleLogout} style={{background:'none',border:'none',cursor:'pointer',color:'#6b7280'}}>
          <LogOut size={18}/>
        </button>
      </div>

      <div style={{maxWidth:'480px',margin:'0 auto',padding:'0 16px 16px'}}>

        {/* Hero Profile Card */}
        <div style={{position:'relative',marginBottom:'16px'}}>
          <div style={{background:'linear-gradient(135deg,#dc2626,#7c3aed)',height:'100px',borderRadius:'20px 20px 0 0'}} />
          <div style={{background:'white',borderRadius:'0 0 20px 20px',padding:'0 20px 20px',boxShadow:'0 4px 20px rgba(0,0,0,0.08)'}}>
            <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginTop:'-36px',marginBottom:'12px'}}>
              <div style={{width:'72px',height:'72px',borderRadius:'50%',background:'white',border:'4px solid white',boxShadow:'0 4px 16px rgba(0,0,0,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'28px'}}>
                👤
              </div>
              <button onClick={openEditDialog} data-testid="edit-profile-btn"
                style={{background:'linear-gradient(135deg,#dc2626,#e11d48)',border:'none',borderRadius:'10px',color:'white',padding:'7px 14px',fontWeight:700,fontSize:'0.75rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'4px'}}>
                <Pencil size={12}/> Edit
              </button>
            </div>
            <h2 style={{fontWeight:800,fontSize:'1.1rem',color:'#111827',fontFamily:"'Syne',sans-serif",marginBottom:'4px'}}>{userName || 'Customer'}</h2>
            {userPhone && <p style={{fontSize:'0.78rem',color:'#6b7280',display:'flex',alignItems:'center',gap:'4px'}}><Phone size={12}/>{userPhone}</p>}
            {userEmail && <p style={{fontSize:'0.75rem',color:'#6b7280',display:'flex',alignItems:'center',gap:'4px',marginTop:'2px'}}><Mail size={11}/>{userEmail}</p>}
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px',marginBottom:'16px'}}>
          {[
            {icon:'📅',val:bookings.length,lbl:'Bookings',color:'#2563eb',bg:'#eff6ff'},
            {icon:'❤️',val:favorites.length,lbl:'Favourites',color:'#dc2626',bg:'#fef2f2'},
            {icon:'⭐',val:loyaltyPoints,lbl:'Points',color:'#d97706',bg:'#fefce8'},
          ].map(({icon,val,lbl,color,bg})=>(
            <div key={lbl} style={{background:'white',borderRadius:'16px',padding:'14px 10px',textAlign:'center',boxShadow:'0 2px 8px rgba(0,0,0,0.06)',border:'1px solid #f3f4f6'}}>
              <div style={{fontSize:'20px',marginBottom:'4px'}}>{icon}</div>
              <div style={{fontWeight:800,fontSize:'1.2rem',color}}>{val}</div>
              <div style={{fontSize:'0.62rem',color:'#9ca3af'}}>{lbl}</div>
            </div>
          ))}
        </div>

        {/* Menu Items */}
        <div style={{display:'flex',flexDirection:'column',gap:'10px',marginBottom:'16px'}}>
          {[
            {icon:'📅',title:'My Bookings',sub:`${bookings.length} total bookings`,action:()=>navigate('/customer')},
            {icon:'❤️',title:'Favourite Salons',sub:`${favorites.length} saved salons`,action:()=>navigate('/customer')},
            {icon:'🏆',title:'Loyalty Points',sub:`${loyaltyPoints} points earned`,action:()=>{}},
            {icon:'❓',title:'Help Center',sub:'FAQs & support',action:()=>navigate('/help')},
          ].map(({icon,title,sub,action})=>(
            <button key={title} onClick={action}
              style={{display:'flex',alignItems:'center',gap:'14px',background:'white',border:'none',borderRadius:'16px',padding:'14px',cursor:'pointer',textAlign:'left',boxShadow:'0 2px 8px rgba(0,0,0,0.05)',border:'1px solid #f3f4f6',width:'100%',transition:'all 0.2s ease'}}
              onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)'}
              onMouseLeave={e=>e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.05)'}
            >
              <div style={{width:'40px',height:'40px',borderRadius:'12px',background:'#fef2f2',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px',flexShrink:0}}>{icon}</div>
              <div style={{flex:1}}>
                <p style={{fontWeight:700,fontSize:'0.85rem',color:'#111827'}}>{title}</p>
                <p style={{fontSize:'0.72rem',color:'#9ca3af',marginTop:'1px'}}>{sub}</p>
              </div>
              <span style={{color:'#d1d5db',fontSize:'1rem'}}>›</span>
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <button onClick={()=>navigate('/customer')}
            style={{background:'linear-gradient(135deg,#dc2626,#e11d48)',color:'white',border:'none',borderRadius:'14px',padding:'14px',fontWeight:800,fontSize:'0.9rem',cursor:'pointer',boxShadow:'0 4px 16px rgba(220,38,38,0.3)'}}>
            ✂️ Book New Service
          </button>
          <button onClick={handleLogout}
            style={{background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca',borderRadius:'14px',padding:'12px',fontWeight:700,fontSize:'0.85rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
            <LogOut size={16}/> Logout
          </button>
        </div>
      </div>

      {/* EDIT PROFILE DIALOG */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="bg-red-100 p-2 rounded-lg">
                <Pencil className="w-5 h-5 text-red-600" />
              </div>
              Edit Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                <User className="w-4 h-4 inline mr-1" />
                Name
              </label>
              <Input
                data-testid="edit-name-input"
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                placeholder="Enter your name"
                className="border-gray-300 focus:border-red-500"
              />
            </div>

            {/* Phone Field (Read-only) */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number
              </label>
              <Input
                value={userPhone}
                disabled
                className="bg-gray-100 text-gray-500"
              />
              <p className="text-xs text-gray-400 mt-1">Phone number cannot be changed</p>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                <Mail className="w-4 h-4 inline mr-1" />
                Email (Optional)
              </label>
              <Input
                type="email"
                data-testid="edit-email-input"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                placeholder="Enter your email"
                className="border-gray-300 focus:border-red-500"
              />
            </div>

            {/* Address Field */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                <MapPin className="w-4 h-4 inline mr-1" />
                Address (Optional)
              </label>
              <Input
                data-testid="edit-address-input"
                value={editForm.address}
                onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                placeholder="Enter your address"
                className="border-gray-300 focus:border-red-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveProfile}
                className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Save Changes
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CustomerProfile;
